// @ts-nocheck
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Settings, User, ChevronDown, ShieldCheck, X, Coins, Truck } from "lucide-react";
import { logout } from "../../features/auth/authSlice";
import { returnPath } from "../../features/auth/returnPath";
import { useAuth } from "../../hooks/useAuth";
import { TopPerformersWidget } from "../../components/TopPerformersWidget";
import { DriverLeaveModal } from "../../components/modals/DriverLeaveModal";
import { CurrencyConverterModal } from "../../components/modals/CurrencyConverterModal";
import { TruckDriverBookModal } from "../../components/modals/TruckDriverBookModal";
import { StatementReplyBell } from "../../features/customer-statement/components/StatementReplyBell";
import { NotificationBell } from "../../features/employee-notifications";
import { MyUnreadMailBadge } from "../../features/mail-monitor/mine/components/MyUnreadMailBadge";

// Assets
import Logo from "../../assets/login/appLogo.svg";

// The one map, shared with the login redirect and DashboardRouter. The
// header used to carry its own copy of it, which had already drifted: it
// knew nothing of transport (1000) or forwarding (1500), sent 1200 to the
// shared accounts dashboard instead of the receivable desk, and ignored the
// permissionId that splits 1300 between the two maintenance desks.
import { getDashboardPath } from "../../config/dashboardRoutes";

export function Header() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { userName, roleName, roleId, permissionId } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isDriverLeaveModalOpen, setIsDriverLeaveModalOpen] = useState(false);
  const [isCurrencyConverterOpen, setIsCurrencyConverterOpen] = useState(false);
  const [isTruckDriverBookOpen, setIsTruckDriverBookOpen] = useState(false);

  const displayName = userName || "Admin";
  const displayRole = roleName || (roleId ? `Role ${roleId}` : "User");
  const todayLabel = useMemo(
    () =>
      new Date().toLocaleDateString("en-IN", {
        weekday: "short",
        day: "2-digit",
        month: "short",
        year: "numeric",
      }),
    []
  );

  const handleLogout = () => {
    // Leaving on purpose: the next login starts at the dashboard.
    returnPath.clear();
    dispatch(logout());
    navigate("/");
  };

  return (
    <header
      className="h-12 flex items-center justify-between px-3 sm:px-4 border-b glass-header select-none-touch"
      style={{
        borderColor: "var(--color-border)",
        boxShadow: "var(--shadow-sm)",
        zIndex: "var(--z-sticky)",
      }}
    >
      {/* LEFT: Logo + Brand + Quick Actions */}
      <div className="flex items-center gap-2 sm:gap-3">
        <button
          type="button"
          onClick={() => navigate(getDashboardPath(roleId, permissionId))}
          className="flex items-center gap-2 rounded-lg px-1.5 py-1 transition-all hover:bg-slate-100/80 active:scale-95"
          title="Go to Dashboard"
        >
          <div className="w-7 h-7 rounded-lg flex items-center justify-center">
            <img src={Logo} alt="Maleva Logo" className="w-7 h-7" />
          </div>
          <div className="leading-tight text-left">
            <h1
              className="text-[14px] sm:text-[15px] font-bold tracking-tight"
              style={{ color: "var(--color-text-primary)", lineHeight: "1.2" }}
            >
              MALEVA
            </h1>
            <p
              className="text-[9px] sm:text-[10px] font-medium"
              style={{ color: "var(--color-primary-600)", lineHeight: "1" }}
            >
              SHIP SPARE IN TRANSIT
            </p>
          </div>
        </button>

        <div className="hidden sm:flex items-center gap-1.5 sm:gap-2">
          <button
            onClick={() => setIsDriverLeaveModalOpen(true)}
            className="px-2.5 py-1.5 text-[11px] sm:text-[12px] font-bold rounded-lg bg-blue-50/90 text-blue-600 border border-blue-200/80 hover:bg-blue-100 hover:text-blue-700 transition-colors active:scale-95"
          >
            Driver Leave Detail
          </button>

          <button
            onClick={() => setIsTruckDriverBookOpen(true)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] sm:text-[12px] font-bold rounded-lg bg-amber-50/90 text-amber-700 border border-amber-200/80 hover:bg-amber-100 hover:text-amber-800 transition-colors active:scale-95"
            title="Which driver is on which truck, and how to reach them"
          >
            <Truck className="w-3.5 h-3.5" />
            <span className="hidden md:inline">Truck &amp; Driver</span>
            <span className="md:hidden">Trucks</span>
          </button>

          <button
            onClick={() => setIsCurrencyConverterOpen(true)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] sm:text-[12px] font-bold rounded-lg bg-emerald-50/90 text-emerald-700 border border-emerald-200/80 hover:bg-emerald-100 hover:text-emerald-800 transition-colors active:scale-95"
            title="Convert any currency to Malaysian Ringgit"
          >
            <Coins className="w-3.5 h-3.5" />
            <span className="hidden lg:inline">Currency Converter</span>
            <span className="lg:hidden">Currency</span>
          </button>
        </div>
      </div>

      {/* CENTER: Date Strip (Shown on wide desktop to prevent tablet crowding) */}
      <div className="hidden xl:flex flex-1 mx-6 justify-center">
        <div
          className="rounded-lg px-3.5 py-1 border flex items-center justify-center bg-slate-50/70"
          style={{ borderColor: "var(--color-border-light)" }}
        >
          <span
            className="text-[12px] font-semibold tracking-wide"
            style={{ color: "var(--color-text-secondary)" }}
          >
            {todayLabel}
          </span>
        </div>
      </div>

      {/* RIGHT: Actions + User Profile */}
      <div className="flex items-center gap-1">
        {/* Customers' replies to their statements — shown only to roles on the notify list */}
        <StatementReplyBell />

        {/* The employee's own unread company mail - shown only to people linked to a mailbox */}
        <MyUnreadMailBadge />

        {/* The employee's notices (forwarding request steps); replaces the old placeholder bell */}
        <NotificationBell />

        {/* Settings */}
        <button
          className="p-1.5 rounded-lg transition-all duration-200"
          style={{ color: "var(--color-text-tertiary)" }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "var(--color-hover-overlay)";
            e.currentTarget.style.color = "var(--color-text-secondary)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
            e.currentTarget.style.color = "var(--color-text-tertiary)";
          }}
        >
          <Settings className="w-4 h-4" />
        </button>

        {/* User Profile */}
        <div className="relative ml-1">
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="flex items-center gap-2 px-2 py-1.5 rounded-lg transition-all duration-200"
            style={{
              backgroundColor: isDropdownOpen
                ? "var(--color-hover-overlay)"
                : "transparent",
            }}
            onMouseEnter={(e) => {
              if (!isDropdownOpen) {
                e.currentTarget.style.backgroundColor = "var(--color-hover-overlay)";
              }
            }}
            onMouseLeave={(e) => {
              if (!isDropdownOpen) {
                e.currentTarget.style.backgroundColor = "transparent";
              }
            }}
          >
            <div
              className="w-7 h-7 rounded-lg flex items-center justify-center"
              style={{
                background:
                  "linear-gradient(135deg, var(--color-primary-500), var(--color-primary-600))",
                boxShadow: "var(--shadow-sm)",
              }}
            >
              <User className="w-4 h-4 text-white" />
            </div>

            <div className="text-left hidden sm:block">
              <p
                className="text-[13px] font-semibold leading-tight"
                style={{ color: "var(--color-text-secondary)" }}
              >
                {displayName}
              </p>
              <p
                className="text-[10px] font-medium leading-tight mt-0.5"
                style={{ color: "var(--color-primary-600)" }}
              >
                {displayRole}
              </p>
            </div>
            
            <ChevronDown
              className={`w-3.5 h-3.5 transition-transform duration-200 ${
                isDropdownOpen ? "rotate-180" : ""
              }`}
              style={{ color: "var(--color-text-quaternary)" }}
            />
          </button>

          {/* Premium Dropdown */}
          {isDropdownOpen && (
            <div
              className="absolute right-0 mt-2 w-72 rounded-2xl border animate-scale-in"
              style={{
                backgroundColor: "var(--color-surface)",
                borderColor: "var(--color-border)",
                boxShadow: "var(--shadow-xl)",
                zIndex: "var(--z-dropdown)",
              }}
            >
              <div
                className="px-6 py-4 border-b"
                style={{ borderColor: "var(--color-border-light)" }}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{
                      background:
                        "linear-gradient(135deg, var(--color-primary-500), var(--color-primary-600))",
                      boxShadow: "var(--shadow-sm)",
                    }}
                  >
                    <User className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p
                      className="text-sm font-semibold"
                      style={{ color: "var(--color-text-primary)" }}
                    >
                      {displayName}
                    </p>
                    <p
                      className="text-xs"
                      style={{ color: "var(--color-primary-700)" }}
                    >
                      {displayRole}
                    </p>
                    <div
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium mt-2"
                      style={{
                        backgroundColor: "var(--color-success-light)",
                        color: "var(--color-success-700)",
                      }}
                    >
                      <ShieldCheck className="w-3.5 h-3.5" />
                      Online
                    </div>
                  </div>
                </div>
              </div>

              <div className="py-2">
                <button
                  onClick={() => { navigate("/MyProfile"); setIsDropdownOpen(false); }}
                  className="flex items-center w-full px-6 py-3 text-sm transition-colors duration-200"
                  style={{ color: "var(--color-text-secondary)" }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "var(--color-hover-overlay)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  <User
                    className="w-4 h-4 mr-3"
                    style={{ color: "var(--color-text-quaternary)" }}
                  />
                  My Profile
                </button>

                <button
                  onClick={() => { navigate("/MyProfile"); setIsDropdownOpen(false); }}
                  className="flex items-center w-full px-6 py-3 text-sm transition-colors duration-200"
                  style={{ color: "var(--color-text-secondary)" }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "var(--color-hover-overlay)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  <Settings
                    className="w-4 h-4 mr-3"
                    style={{ color: "var(--color-text-quaternary)" }}
                  />
                  Account Settings
                </button>
              </div>

              <div
                className="border-t"
                style={{ borderColor: "var(--color-border-light)" }}
              >
                <button
                  onClick={handleLogout}
                  className="flex items-center w-full px-6 py-3 text-sm transition-colors duration-200 rounded-b-2xl"
                  style={{ color: "var(--color-danger)" }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "var(--color-danger-light)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  <svg
                    className="w-4 h-4 mr-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    />
                  </svg>
                  Sign Out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Driver Leave Modal */}
      {isDriverLeaveModalOpen && (
        <DriverLeaveModal onClose={() => setIsDriverLeaveModalOpen(false)} />
      )}
      {/* Truck and driver contact book */}
      {isTruckDriverBookOpen && (
        <TruckDriverBookModal onClose={() => setIsTruckDriverBookOpen(false)} />
      )}
      {/* Currency Converter Modal */}
      {isCurrencyConverterOpen && (
        <CurrencyConverterModal onClose={() => setIsCurrencyConverterOpen(false)} />
      )}
    </header>
  );
}
